import { z } from "zod";

// Search configuration
export type MessageSearchType = 'vector' | 'keyword';

// API error response
export const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

// Message roles matching backend enum
export const MessageRoleSchema = z.enum([
  "user",
  "assistant",
  "system",
  "tool",
]);
export type MessageRole = z.infer<typeof MessageRoleSchema>;

// Compact conversation-grade rollup shown in the thread list.
export const ThreadGradeSummarySchema = z.object({
  // Sum of the 4 dimension averages (4–20); null until graded.
  score_out_of_20: z.number().nullable(),
  // True if any turn scored ≤2 on any dimension.
  needs_review: z.boolean(),
  graded_at: z.string().nullable().optional(),
});

export type ThreadGradeSummary = z.infer<typeof ThreadGradeSummarySchema>;

// Thread schemas for admin view
export const AdminThreadSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string(),
  member_id: z.string().uuid().nullable().optional(),
  title: z.string().nullable().optional(),
  persona_id: z.number().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  data: z.record(z.unknown()).nullable().optional(),
  greeting: z.string().nullable().optional(),
  message_count: z.number().optional(),
  user_display_name: z.string().nullable().optional(),
  flagged_harmful: z.boolean(),
  upvote_count: z.number(),
  downvote_count: z.number(),
  has_governor_rejection: z.boolean(),
  persona_name: z.string().nullable().optional(),
  grade: ThreadGradeSummarySchema.nullable().optional(),
  ai_summary: z.string().nullable().optional(),
  ai_summary_at: z.string().nullable().optional(),
});

export type AdminThread = z.infer<typeof AdminThreadSchema>;

export const ToolCallSchema = z.object({
  id: z.string(),
  function: z.object({
    name: z.string(),

    // stringified json
    arguments: z.string(),
  }),
});

// Message schemas for admin view
export const AdminMessageSchema = z.object({
  id: z.number(),
  thread_id: z.string().uuid(),
  role: MessageRoleSchema,
  content: z.string().nullable().optional(),
  token_count: z.number().nullable().optional(),
  tool_calls: z.array(ToolCallSchema).nullable().optional(),
  thinking_duration: z.number().nullable().optional(),
  tool_duration_seconds: z.number().nullable().optional(),
  is_summary: z.boolean(),
  created_at: z.string(),
  was_helpful: z.boolean().nullable().optional(),
  governor_passed: z.boolean().nullable().optional(),
  governor_violations: z.array(z.string()).nullable().optional(),
});

export type AdminMessage = z.infer<typeof AdminMessageSchema>;

// Paginated threads response
export const PaginatedThreadsResponseSchema = z.array(AdminThreadSchema);

export type PaginatedThreadsResponse = z.infer<
  typeof PaginatedThreadsResponseSchema
>;

// Governor rejection details (rejected LLM responses)
export const GovernorRejectionSchema = z.object({
  id: z.number(),
  message_id: z.number(),
  user_message: z.string(),
  rejected_response: z.string(),
  violations: z.array(z.string()),
  created_at: z.string(),
  false_positive: z.boolean().nullable().optional(),
  false_positive_note: z.string().nullable().optional(),
  false_positive_by: z.string().nullable().optional(),
  false_positive_at: z.string().nullable().optional(),
});

export type GovernorRejection = z.infer<typeof GovernorRejectionSchema>;

// Governor config (singleton settings)
export const GovernorConfigResponseSchema = z.object({
  enabled: z.boolean(),
  system_prompt: z.string(),
  deployment_name: z.string().nullable(),
  max_retries: z.number().int(),
  max_tokens: z.number().int(),
  rejected_response_message: z.string().nullable(),
  max_regeneration_retries: z.number().int(),
  reasoning_effort: z.string().nullable(),
  verbosity: z.string().nullable(),
  updated_at: z.string(),
  updated_by: z.string().nullable(),
});

export type GovernorConfigResponse = z.infer<
  typeof GovernorConfigResponseSchema
>;

export interface GovernorConfigUpdate {
  enabled?: boolean | null;
  system_prompt?: string | null;
  deployment_name?: string | null;
  max_retries?: number | null;
  max_tokens?: number | null;
  rejected_response_message?: string | null;
  max_regeneration_retries?: number | null;
  reasoning_effort?: string | null;
  verbosity?: string | null;
}

// Runtime dashboard feature flags, served by GET /config/ui from server-side
// env vars (so they can be toggled per-environment without rebuilding the dash).
export const UiConfigSchema = z.object({
  show_conversation_grades: z.boolean(),
  show_eval_runs: z.boolean(),
  show_eval_suggestions: z.boolean(),
  show_prompt_lab: z.boolean(),
});

export type UiConfig = z.infer<typeof UiConfigSchema>;

// Thread with messages response
export const ThreadWithMessagesResponseSchema = z.object({
  thread: AdminThreadSchema,
  messages: z.array(AdminMessageSchema),
  rejections: z.array(GovernorRejectionSchema).optional(),
});

export type ThreadWithMessagesResponse = z.infer<
  typeof ThreadWithMessagesResponseSchema
>;

// Filter parameters for thread list
export interface ThreadFilters {
  user_display_name?: string;
  date_from?: string;
  date_to?: string;
  flagged_harmful?: boolean;
  has_governor_rejection?: boolean;
  vote_filter?: "upvoted" | "downvoted";
  is_test_user?: boolean;
  // Hide threads that never received a user-role message — chat
  // sessions that were opened but never had any input. Useful for
  // focusing on real conversations.
  hide_empty?: boolean;
  limit?: number;
  offset?: number;
}

export const ThreadSummaryResponseSchema = z.object({
  thread_id: z.string().uuid(),
  summary: z.string(),
});

export type ThreadSummaryResponse = z.infer<typeof ThreadSummaryResponseSchema>;

const ScoreMapSchema = z.object({
  factual_accuracy: z.number().nullable(),
  completeness: z.number().nullable(),
  tone_safety: z.number().nullable(),
  kb_grounding: z.number().nullable(),
});

const MatchedBucketSchema = z.object({
  name: z.string(),
  scenario_count: z.number().int(),
});

export const TurnGradeSchema = z.object({
  turn_index: z.number().int(),
  user_message: z.string(),
  assistant_response: z.string(),
  matched_buckets: z.array(MatchedBucketSchema),
  scores: ScoreMapSchema.nullable(),
  scoring_notes: z.string(),
  // Router's one-sentence explanation for the bucket choice.
  routing_rationale: z.string().optional(),
});

export const GradeWeakestSchema = z.object({
  bucket: z.string().nullable(),
  dimension: z.string(),
  score: z.number(),
  turn_index: z.number().int(),
});

export const GradeFeedbackSchema = z.object({
  vote: z.enum(['up', 'down']),
  feedback: z.string().nullable().optional(),
  reviewer_email: z.string().nullable().optional(),
  created_at: z.string(),
});

export const ThreadGradeResponseSchema = z.object({
  thread_id: z.string().uuid(),
  turn_grades: z.array(TurnGradeSchema),
  aggregate: ScoreMapSchema,
  bucket_coverage: z.record(z.string(), z.number().int()),
  selection_mode: z.enum(['auto', 'manual']),
  graded_at: z.string(),
  score_out_of_20: z.number().nullable().optional(),
  needs_review: z.boolean().optional(),
  weakest: GradeWeakestSchema.nullable().optional(),
  feedback: GradeFeedbackSchema.nullable().optional(),
});

export type MatchedBucket = z.infer<typeof MatchedBucketSchema>;
export type TurnGrade = z.infer<typeof TurnGradeSchema>;
export type ThreadGradeResponse = z.infer<typeof ThreadGradeResponseSchema>;
export type GradeFeedback = z.infer<typeof GradeFeedbackSchema>;

// Persona schemas
export const PersonaResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  system_prompt: z.string(),
  deployment_name: z.string().nullable(),
  temperature: z.number().nullable(),
  reasoning_effort: z.string().nullable(),
  verbosity: z.string().nullable(),
  max_tokens: z.number().int().positive().nullable(),
  is_enabled: z.boolean(),
  eager_document_search: z.boolean(),
  tool_messages: z.record(z.string(), z.string()).nullable(),
  // temp: shouldn't really be optional
  intro_type: z.string().nullable().optional(),
  greeting_prompt: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type PersonaResponse = z.infer<typeof PersonaResponseSchema>;

export interface PersonaCreate {
  name: string;
  system_prompt: string;
  description?: string | null;
  deployment_name?: string | null;
  temperature?: number | null;
  reasoning_effort?: string | null;
  verbosity?: string | null;
  max_tokens?: number | null;
  eager_document_search?: boolean;
  tool_messages?: Record<string, string> | null;
  intro_type: string;
  greeting_prompt?: string | null;
}

export interface PersonaUpdate {
  name?: string | null;
  system_prompt?: string | null;
  description?: string | null;
  deployment_name?: string | null;
  temperature?: number | null;
  reasoning_effort?: string | null;
  verbosity?: string | null;
  max_tokens?: number | null;
  is_enabled?: boolean | null;
  eager_document_search?: boolean | null;
  tool_messages?: Record<string, string> | null;
  intro_type?: string;
  greeting_prompt?: string | null;
}

// Persona token estimate
export const TokenEstimateResponseSchema = z.object({
  persona_id: z.number(),
  system_prompt_tokens: z.number(),
  system_message_tokens: z.number(),
  context_token_limit: z.number(),
  context_percentage: z.number(),
});

export type TokenEstimateResponse = z.infer<typeof TokenEstimateResponseSchema>;

// Knowledge article schemas
export const KnowledgeArticleResponseSchema = z.object({
  id: z.number(),
  title: z.string(),
  content: z.string(),
  version: z.number().optional(),
  hit_count: z.number(),
  is_enabled: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type KnowledgeArticleResponse = z.infer<
  typeof KnowledgeArticleResponseSchema
>;

export const KnowledgeArticleVersionSchema = z.object({
  version: z.number(),
  title: z.string(),
  diff: z.string().nullable(),
  content: z.string(),
  hit_count: z.number(),
  created_at: z.string(),
});

export type KnowledgeArticleVersion = z.infer<
  typeof KnowledgeArticleVersionSchema
>;

export interface KnowledgeArticleCreate {
  title: string;
  content: string;
}

export interface KnowledgeArticleUpdate {
  title?: string | null;
  content?: string | null;
  is_enabled?: boolean | null;
}

export interface KnowledgeArticleRollback {
  version: number;
}

// Knowledge query (vector search)
export const KnowledgeQueryChunkResultSchema = z.object({
  article_id: z.number(),
  article_title: z.string(),
  chunk_index: z.number(),
  chunk_content: z.string(),
  similarity: z.number().nullable().optional(),
  rrf_score: z.number().nullable().optional(),
});

export type KnowledgeQueryChunkResult = z.infer<
  typeof KnowledgeQueryChunkResultSchema
>;

export const KnowledgeQueryResponseSchema = z.object({
  results: z.array(KnowledgeQueryChunkResultSchema),
  total: z.number(),
});

export type KnowledgeQueryResponse = z.infer<
  typeof KnowledgeQueryResponseSchema
>;

// Knowledge import result
export const KnowledgeImportErrorSchema = z.object({
  filename: z.string(),
  error: z.string(),
});

export const KnowledgeImportResultSchema = z.object({
  created: z.number(),
  updated: z.number(),
  errors: z.array(KnowledgeImportErrorSchema),
});

export type KnowledgeImportResult = z.infer<typeof KnowledgeImportResultSchema>;

// ---------------------------------------------------------------------------
// Eval Runs (dash-triggered evaluator)
// ---------------------------------------------------------------------------

export const EvalRunStatusSchema = z.enum([
  "pending",
  // Waiting on the API's process-wide eval serialization lock — only one
  // eval run executes at a time so concurrent runs don't split the shared
  // Azure deployment's throughput.
  "queued",
  "running",
  "completed",
  "failed",
]);
export type EvalRunStatus = z.infer<typeof EvalRunStatusSchema>;

export const AvailableScenarioSchema = z.object({
  path: z.string(),
  name: z.string(),
  scenario_count: z.number().int(),
});
export type AvailableScenario = z.infer<typeof AvailableScenarioSchema>;

export const EvalRunSummarySchema = z.object({
  id: z.number().int(),
  created_by_user_id: z.string(),
  persona_id: z.number().int().nullable(),
  persona_name: z.string().nullable().optional(),
  // Deployment_name of the persona at response time. Used for filtering the
  // history list; not guaranteed to match the model used at run time if the
  // persona was edited after the run.
  model: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  status: EvalRunStatusSchema,
  // Derived server-side from scenario_paths. The list page renders this
  // as a pill so users can see at a glance which run-shape produced the
  // row's scores. "ad_hoc" = user-picked scenario subset. Optional so the
  // dash tolerates an older API that doesn't yet send the field (staged
  // deploys, or a dev server that wasn't reloaded after the schema
  // change). Call sites coalesce to 'ad_hoc' for the undefined case.
  run_type: z.enum(['smoke', 'full', 'ad_hoc']).optional(),
  total_scenarios: z.number().int(),
  completed_scenarios: z.number().int(),
  passed_scenarios: z.number().int(),
  // Included on the summary so the Prompt Lab source-eval dropdown can
  // filter to only smoke-set-compatible past runs.
  scenario_paths: z.array(z.string()),
  avg_e2e_latency_ms: z.number().nullable().optional(),
  avg_cost_usd: z.number().nullable().optional(),
  total_cost_usd: z.number().nullable().optional(),
  // All four grader avgs on the summary so the history list can render an
  // aggregate "score / 20" without fetching each run's full detail.
  avg_factual_accuracy: z.number().nullable().optional(),
  avg_completeness: z.number().nullable().optional(),
  avg_tone_safety: z.number().nullable().optional(),
  avg_kb_grounding: z.number().nullable().optional(),
  // True between Stop click and actual termination. The UI renders
  // "stopping…" so the user knows the request was received while in-flight
  // scenarios drain.
  cancel_requested: z.boolean(),
  started_at: z.string(),
  completed_at: z.string().nullable().optional(),
});
export type EvalRunSummary = z.infer<typeof EvalRunSummarySchema>;

// A per-scenario row inside an EvalRun. Fields are loose because the JSONB
// blob mirrors the in-process runner output; we render defensively.
export const EvalRunScenarioResultSchema = z.object({
  scenario_name: z.string(),
  passed: z.boolean(),
  error: z.string().nullable().optional(),
  content_filtered: z.boolean().optional(),
  message: z.string().nullable().optional(),
  response: z.string().nullable().optional(),
  golden_response: z.string().nullable().optional(),
  actual_tool_calls: z.array(z.string()).optional(),
  expected_tool_calls: z.array(z.string()).optional(),
  end_to_end_ms: z.number().nullable().optional(),
  latency_breakdown: z.string().optional(),
  prompt_tokens: z.number().optional(),
  completion_tokens: z.number().optional(),
  cached_tokens: z.number().optional(),
  total_tokens: z.number().optional(),
  cost_usd: z.number().nullable().optional(),
  kb_article_titles: z.array(z.string()).optional(),
  factual_accuracy: z.union([z.string(), z.number()]).optional(),
  completeness: z.union([z.string(), z.number()]).optional(),
  tone_safety: z.union([z.string(), z.number()]).optional(),
  kb_grounding: z.union([z.string(), z.number()]).optional(),
  scoring_notes: z.string().optional(),
  trace_id: z.string().nullable().optional(),
});
export type EvalRunScenarioResult = z.infer<typeof EvalRunScenarioResultSchema>;

export const EvalRunDetailSchema = EvalRunSummarySchema.extend({
  // The four grader avgs now live on the summary too — kept here only to
  // document the contract; they're inherited via .extend().
  scenario_paths: z.array(z.string()),
  error: z.string().nullable().optional(),
  p95_e2e_latency_ms: z.number().nullable().optional(),
  results: z.array(EvalRunScenarioResultSchema),
});
export type EvalRunDetail = z.infer<typeof EvalRunDetailSchema>;

export interface EvalRunCreate {
  persona_id?: number | null;
  scenario_paths: string[];
  notes?: string | null;
  // Also run every approved eval suggestion as part of this run. Default true.
  include_suggested_evals?: boolean;
}

// ---------------------------------------------------------------------------
// Auto-suggested evals
// ---------------------------------------------------------------------------

export const SuggestionTriggerSchema = z.enum([
  "fix_forward",
  "lock_in",
  "novel",
  "manual",
]);
export type SuggestionTrigger = z.infer<typeof SuggestionTriggerSchema>;

export const SuggestionStatusSchema = z.enum([
  "candidate",
  "trusted",
  "dismissed",
]);
export type SuggestionStatus = z.infer<typeof SuggestionStatusSchema>;

export const EvalSuggestionSummarySchema = z.object({
  id: z.number().int(),
  trigger_kind: SuggestionTriggerSchema,
  status: SuggestionStatusSchema,
  target_bucket: z.string().nullable().optional(),
  name: z.string(),
  message: z.string(),
  rationale: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  occurrence_count: z.number().int(),
  review_score: z.number().nullable().optional(),
  review_concern_count: z.number().int(),
  source_thread_id: z.string().nullable().optional(),
  source_turn_index: z.number().int().nullable().optional(),
  created_by: z.string().nullable().optional(),
  reviewed_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type EvalSuggestionSummary = z.infer<typeof EvalSuggestionSummarySchema>;

export const EvalSuggestionDetailSchema = EvalSuggestionSummarySchema.extend({
  source_grade_id: z.number().int().nullable().optional(),
  reviewed_at: z.string().nullable().optional(),
  reject_reason: z.string().nullable().optional(),
  // Conversations exhibiting the pattern: [{thread_id, grade_id, turn_index,
  // user_message}].
  evidence: z.array(z.record(z.string(), z.unknown())),
  // Auto-review verdict: {score, concerns:[{lens,severity,detail}], ...}.
  review: z.record(z.string(), z.unknown()).nullable().optional(),
  // The candidate Scenario dict (name/message/golden_response/
  // golden_response_notes/expectations) plus a `_meta` block. Loose on
  // purpose — the form renders defensively.
  draft: z.record(z.string(), z.unknown()),
});
export type EvalSuggestionDetail = z.infer<typeof EvalSuggestionDetailSchema>;

export const SuggestionCountsSchema = z.object({
  candidate: z.number().int(),
  trusted: z.number().int(),
  dismissed: z.number().int(),
});
export type SuggestionCounts = z.infer<typeof SuggestionCountsSchema>;

export const BulkResultSchema = z.object({
  action: z.string(),
  updated: z.number().int(),
});
export type BulkResult = z.infer<typeof BulkResultSchema>;

// signoff → add to eval set (trusted); untrust → back to candidate; dismiss.
export type SuggestionBulkActionKind = "signoff" | "untrust" | "dismiss";

export interface SuggestionBulkRequest {
  ids: number[];
  action: SuggestionBulkActionKind;
  reason?: string | null;
}

export interface SuggestionUpdate {
  draft?: Record<string, unknown> | null;
  target_bucket?: string | null;
}

// ---------------------------------------------------------------------------
// Prompt Lab
// ---------------------------------------------------------------------------

export const PromptExperimentStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
  "promoted",
]);
export type PromptExperimentStatus = z.infer<typeof PromptExperimentStatusSchema>;

export const PromptIterationStatusSchema = z.enum([
  "proposed",
  "running",
  "completed",
  "failed",
]);
export type PromptIterationStatus = z.infer<typeof PromptIterationStatusSchema>;

export const WorstScenarioSummarySchema = z.object({
  scenario_name: z.string(),
  aggregate_score: z.number(),
  factual_accuracy: z.number().nullable().optional(),
  completeness: z.number().nullable().optional(),
  tone_safety: z.number().nullable().optional(),
  kb_grounding: z.number().nullable().optional(),
  member_message: z.string(),
  response: z.string(),
  scoring_notes: z.string(),
});
export type WorstScenarioSummary = z.infer<typeof WorstScenarioSummarySchema>;

export const PromptIterationReadSchema = z.object({
  id: z.number().int(),
  iteration_number: z.number().int(),
  status: PromptIterationStatusSchema,
  error: z.string().nullable().optional(),
  hypothesis: z.string().nullable().optional(),
  diff_text: z.string().nullable().optional(),
  iteration_score: z.number().nullable().optional(),
  parent_persona_id: z.number().nullable().optional(),
  child_persona_id: z.number().nullable().optional(),
  child_persona_name: z.string().nullable().optional(),
  smoke_run_id: z.number().nullable().optional(),
  created_at: z.string(),
  completed_at: z.string().nullable().optional(),
  worst_scenarios: z.array(WorstScenarioSummarySchema).nullable().optional(),
});
export type PromptIterationRead = z.infer<typeof PromptIterationReadSchema>;

export const PromptExperimentSummarySchema = z.object({
  id: z.number().int(),
  created_by_user_id: z.string(),
  baseline_persona_id: z.number().int(),
  baseline_persona_name: z.string().nullable().optional(),
  focus_area: z.string(),
  hypothesis: z.string(),
  budget_iterations: z.number().int(),
  status: PromptExperimentStatusSchema,
  error: z.string().nullable().optional(),
  baseline_smoke_run_id: z.number().nullable().optional(),
  baseline_score: z.number().nullable().optional(),
  // Best iteration_score among real candidates. Populated on list
  // responses; null on detail (the detail page renders per-iteration
  // scores explicitly and doesn't need the rollup).
  best_iteration_score: z.number().nullable().optional(),
  promoted_persona_id: z.number().nullable().optional(),
  promoted_persona_name: z.string().nullable().optional(),
  run_guardrail: z.boolean(),
  baseline_full_suite_run_id: z.number().nullable().optional(),
  baseline_full_score: z.number().nullable().optional(),
  guardrail_run_id: z.number().nullable().optional(),
  guardrail_score: z.number().nullable().optional(),
  started_at: z.string(),
  completed_at: z.string().nullable().optional(),
});
export type PromptExperimentSummary = z.infer<typeof PromptExperimentSummarySchema>;

export const PromptExperimentDetailSchema = PromptExperimentSummarySchema.extend({
  iterations: z.array(PromptIterationReadSchema),
  baseline_worst_scenarios: z.array(WorstScenarioSummarySchema).nullable().optional(),
  // Iteration-set card data: which K scenarios Claude picked, the
  // one-line rationale, and the eval the selector was scoped to.
  picked_scenario_paths: z.array(z.string()).nullable().optional(),
  pick_rationale: z.string().nullable().optional(),
  pick_source_run_id: z.number().int().nullable().optional(),
  pick_source_run_started_at: z.string().nullable().optional(),
  pick_source_run_type: z.enum(['smoke', 'full', 'ad_hoc']).nullable().optional(),
});
export type PromptExperimentDetail = z.infer<typeof PromptExperimentDetailSchema>;

export const PromptExperimentSuggestNextSchema = z.object({
  focus_area: z.string(),
  hypothesis: z.string(),
  best_persona_id: z.number().nullable().optional(),
  best_iteration_id: z.number().nullable().optional(),
  best_score: z.number().nullable().optional(),
});
export type PromptExperimentSuggestNext = z.infer<typeof PromptExperimentSuggestNextSchema>;

export const PromptExperimentExtendResultSchema = z.object({
  budget_iterations: z.number().int(),
  resume_from_iteration: z.number().int(),
});
export type PromptExperimentExtendResult = z.infer<typeof PromptExperimentExtendResultSchema>;

export interface PromptExperimentCreate {
  baseline_persona_id: number;
  /** REQUIRED: the eval_run this experiment is scoped to improve.
   *  Claude picks K iteration scenarios from this run's coverage.
   *  Baseline scores come from this run's results (no fresh baseline
   *  smoke). Must be a completed eval_run for the same persona as
   *  baseline_persona_id. */
  source_eval_run_id: number;
  /** Required UNLESS auto_hypothesis=true. */
  hypothesis: string;
  budget_iterations?: number;
  /** Internal — defaulted server-side to "general"; UI doesn't surface. */
  focus_area?: string;
  /** Run the smoke regression check after the iteration loop
   *  completes (one case per committed scenario file, against both
   *  starting persona and best candidate). */
  run_guardrail?: boolean;
  /** Have Claude write the hypothesis from the source eval's worst rows. */
  auto_hypothesis?: boolean;
  /** Treat `hypothesis` as a SHORT seed and expand it server-side into full
   *  proposer steering, grounded in the source eval's worst rows. */
  expand_hypothesis?: boolean;
}

export const ImprovementSuggestionsSchema = z.object({
  suggestions: z.array(z.string()),
});
export type ImprovementSuggestions = z.infer<typeof ImprovementSuggestionsSchema>;
